import { computed, reactive } from 'vue'
import { buildMemberRemovalWarning, getMemberFullName } from '@pepi/core'
import type { FamilyMember } from '@/types/api'

export interface ConfirmOptions {
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  type?: 'danger' | 'warning' | 'info' | 'success'
  icon?: 'delete' | 'warning' | 'info' | 'question'
  persistent?: boolean
}

export interface ConfirmDialog extends ConfirmOptions {
  id: string
  resolve: (value: boolean) => void
}

interface ConfirmState {
  dialogs: ConfirmDialog[]
}

const state = reactive<ConfirmState>({
  dialogs: []
})

/**
 * Visibilidad derivada del estado COMPARTIDO de módulo. Antes era un
 * ref creado dentro de useConfirm() (uno nuevo por componente): el que
 * llamaba a confirm() encendía SU copia, pero el ConfirmDialog montado
 * en App.vue miraba la suya (siempre false) y el diálogo nunca se pintaba.
 */
const isVisible = computed(() => state.dialogs.length > 0)

let confirmId = 0

/**
 * Composable para confirmaciones y modales de confirmación
 * Proporciona un sistema centralizado para confirmaciones de acciones
 */
export function useConfirm() {

  // ========== MÉTODOS PRINCIPALES ==========

  /**
   * Genera un ID único para el diálogo
   */
  const generateId = () => `confirm-${++confirmId}-${Date.now()}`

  /**
   * Muestra un diálogo de confirmación
   */
  const confirm = (options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      const id = generateId()

      const dialog: ConfirmDialog = {
        id,
        title: options.title,
        message: options.message,
        confirmText: options.confirmText || 'Confirmar',
        cancelText: options.cancelText || 'Cancelar',
        type: options.type || 'info',
        icon: options.icon || 'question',
        persistent: options.persistent || false,
        resolve
      }

      state.dialogs.push(dialog)
    })
  }

  /**
   * Cierra un diálogo específico
   */
  const closeDialog = (id: string, confirmed: boolean = false) => {
    const dialogIndex = state.dialogs.findIndex(d => d.id === id)
    if (dialogIndex > -1) {
      const dialog = state.dialogs[dialogIndex]
      dialog.resolve(confirmed)
      state.dialogs.splice(dialogIndex, 1)
    }
  }

  /**
   * Cierra todos los diálogos
   */
  const closeAll = () => {
    state.dialogs.forEach(dialog => dialog.resolve(false))
    state.dialogs.splice(0)
  }

  // ========== MÉTODOS DE CONVENIENCIA ==========

  /**
   * Confirmación para eliminar un elemento
   */
  const confirmDelete = (itemName: string, itemType: string = 'elemento'): Promise<boolean> => {
    return confirm({
      title: `¿Eliminar ${itemType}?`,
      message: `¿Estás seguro de que deseas eliminar "${itemName}"? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      type: 'danger',
      icon: 'delete'
    })
  }

  /**
   * Confirmación para eliminar familia
   */
  const confirmDeleteFamily = (familyName: string): Promise<boolean> => {
    return confirm({
      title: '¿Eliminar familia?',
      message: `¿Estás seguro de que deseas eliminar la familia "${familyName}"?\n\nEsta acción eliminará:\n• La familia y toda su información\n• Todos los miembros asociados\n• Historial y datos relacionados\n\nEsta acción no se puede deshacer.`,
      confirmText: 'Sí, eliminar familia',
      cancelText: 'Cancelar',
      type: 'danger',
      icon: 'delete'
    })
  }

  /**
   * Confirmación para expulsar (eliminar) un miembro de la familia.
   *
   * EP-26 (B7.5, mr-11): renombrada desde `confirmDeleteMember`. El renombre
   * es intencional — obliga a actualizar a los dos llamantes (`FamilyDetailView.vue`,
   * `FamiliesView.vue`) y no deja ninguno con el texto viejo ("Quitar de la
   * familia" / "no se puede deshacer" sin más), que ya no describe lo que
   * hace el backend desde B6: borra la cuenta y los datos privados de forma
   * irreversible, no solo "quita" al miembro de la familia.
   *
   * El texto lo construye `buildMemberRemovalWarning` (`@pepi/core`, capa de
   * aplicación) a partir del propio `member` — así el aviso distingue solo
   * con datos que ya vienen en la lista de miembros: si tiene cuenta
   * (`hasUserAccount`) y su rol familiar, para la línea de "responsables".
   */
  const confirmRemoveMember = (member: FamilyMember, familyName?: string): Promise<boolean> => {
    const warning = buildMemberRemovalWarning({
      memberName: getMemberFullName(member),
      familyName,
      hasUserAccount: member.hasUserAccount,
      familyRole: member.familyRole,
      isFamilyAdmin: member.isFamilyAdmin
    })
    return confirm({
      title: warning.title,
      message: warning.message,
      confirmText: warning.confirmText,
      cancelText: warning.cancelText,
      type: 'danger',
      icon: 'delete'
    })
  }

  /**
   * Confirmación para salir sin guardar
   */
  const confirmUnsavedChanges = (): Promise<boolean> => {
    return confirm({
      title: '¿Descartar cambios?',
      message: 'Tienes cambios sin guardar. ¿Estás seguro de que deseas salir sin guardar?',
      confirmText: 'Sí, descartar',
      cancelText: 'Continuar editando',
      type: 'warning',
      icon: 'warning'
    })
  }

  /**
   * Confirmación simple sí/no
   */
  const confirmYesNo = (title: string, message: string): Promise<boolean> => {
    return confirm({
      title,
      message,
      confirmText: 'Sí',
      cancelText: 'No',
      type: 'info',
      icon: 'question'
    })
  }

  /**
   * Confirmación de acción importante
   */
  const confirmAction = (actionName: string, details?: string): Promise<boolean> => {
    return confirm({
      title: `¿Confirmar ${actionName.toLowerCase()}?`,
      message: details || `¿Estás seguro de que deseas ${actionName.toLowerCase()}?`,
      confirmText: actionName,
      cancelText: 'Cancelar',
      type: 'warning',
      icon: 'question'
    })
  }

  return {
    // Estado
    isVisible,
    dialogs: state.dialogs,

    // Métodos principales
    confirm,
    closeDialog,
    closeAll,

    // Métodos de conveniencia
    confirmDelete,
    confirmDeleteFamily,
    confirmRemoveMember,
    confirmUnsavedChanges,
    confirmYesNo,
    confirmAction
  }
}